import { cn } from '@plato/ui/util/classes';
import { Children, useEffect, type ReactElement, type ReactNode } from 'react';
import { RadioGroup, RadioGroupItem } from './radio-group.tsx';

export type TabsItemProps = {
    label: string;
    tabId: string;
    children?: ReactNode;
    className?: string;
};

export function TabsItem({ children, className = '' }: TabsItemProps) {
    return <div className={cn('flex', className)}>{children}</div>;
}

type TabNode = ReactElement<TabsItemProps> | undefined | null | false | 0;

type TabsProps = {
    children: TabNode | (TabNode | TabNode[])[];
    className?: string;
    disabled?: boolean;
    onTabSelected?: (tabId: string) => void;
    selectedTab?: string;
    toolbox?: ReactNode;
};

export function Tabs({ children, className, disabled, onTabSelected, selectedTab, toolbox }: TabsProps) {
    const tabElements = Children.toArray(children).filter(Boolean) as ReactElement<TabsItemProps>[];
    const selectedTabElement = tabElements.find((tab) => tab.props.tabId === selectedTab);

    useEffect(() => {
        if (selectedTabElement || !onTabSelected) {
            return;
        }
        const firstTab = tabElements[0]?.props.tabId;
        if (firstTab) {
            onTabSelected(firstTab);
        }
    }, [onTabSelected, selectedTabElement, tabElements]);

    if (tabElements.length === 0) {
        return <></>;
    }

    return (
        <div className={cn('flex w-full flex-col gap-3', className)}>
            <div className="flex justify-between flex-col sm:flex-row gap-3">
                <RadioGroup
                    value={selectedTab}
                    onValueChange={onTabSelected}
                    disabled={disabled}
                    className="flex overflow-x-auto scrollbar-none"
                >
                    {tabElements.map((tab) => (
                        <RadioGroupItem key={tab.props.tabId} value={tab.props.tabId}>
                            {tab.props.label}
                        </RadioGroupItem>
                    ))}
                </RadioGroup>
                {toolbox}
            </div>

            {selectedTabElement}
        </div>
    );
}
